'use client'

import { useMemo, useState } from 'react'
import { History, ArrowDownLeft, ArrowUpRight, FileText, CreditCard } from 'lucide-react'
import {
  DashboardData,
  DashboardPayment,
  DocModule,
  MODULE_META,
  DOC_MODULE_ORDER,
  formatDateTime,
  formatCredits,
  formatMoney,
} from '../types'

interface Props {
  data: DashboardData
  /** Paiements confirmés — source DISTINCTE de `data`, chargée séparément
   *  depuis /api/payment/history. Voir `DashboardPayment` : leur `amount` est
   *  une somme d'ARGENT, pas un nombre de crédits. */
  payments: DashboardPayment[]
}

type Filter = 'all' | 'payments' | 'credits' | DocModule

interface FeedItem {
  id: string
  kind: 'payment' | 'credit' | 'document'
  module?: DocModule
  title: string
  subtitle?: string
  /** Crédits (`kind: 'credit'`) — signé. */
  amount?: number
  /** Argent (`kind: 'payment'`) — toujours accompagné de sa devise. */
  money?: { amount: number; currency: string }
  date: string
}

export default function HistorySection({ data, payments }: Props) {
  const [filter, setFilter] = useState<Filter>('all')

  const feed = useMemo<FeedItem[]>(() => {
    const items: FeedItem[] = []

    // Paiements — montants en ARGENT. Volontairement construits à part des
    // mouvements de crédits ci-dessous : les deux ne se comparent pas.
    for (const p of payments) {
      items.push({
        id: `pay-${p.id}`,
        kind: 'payment',
        title: 'Abonnement Pro',
        subtitle: [p.provider, p.method].filter(Boolean).join(' · ') || undefined,
        money: { amount: p.amount, currency: p.currency },
        date: p.created_at,
      })
    }

    for (const m of data.movements) {
      items.push({
        id: `tx-${m.id}`,
        kind: 'credit',
        title: m.description || (m.amount > 0 ? 'Recharge de crédits' : 'Consommation'),
        amount: m.amount,
        date: m.created_at,
      })
    }

    for (const mod of DOC_MODULE_ORDER) {
      for (const doc of data.documents[mod] ?? []) {
        items.push({
          id: `doc-${mod}-${doc.id}`,
          kind: 'document',
          module: mod,
          title: doc.title,
          subtitle: MODULE_META[mod].label,
          date: doc.created_at,
        })
      }
    }

    return items.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
  }, [data, payments])

  const filtered = feed.filter((it) => {
    if (filter === 'all') return true
    if (filter === 'payments') return it.kind === 'payment'
    if (filter === 'credits') return it.kind === 'credit'
    return it.module === filter
  })

  const filterChips: { key: Filter; label: string }[] = [
    { key: 'all', label: 'Tout' },
    // « Paiements » en tête : c'est ce qu'un utilisateur vient chercher en
    // premier dans un historique, avant ses mouvements de crédits.
    { key: 'payments', label: '🧾 Paiements' },
    { key: 'credits', label: '💳 Crédits' },
    ...DOC_MODULE_ORDER.map((m) => ({ key: m as Filter, label: `${MODULE_META[m].emoji} ${MODULE_META[m].label}` })),
  ]

  return (
    <div className="space-y-4">
      {/* Filtres */}
      <div className="flex gap-1.5 overflow-x-auto pb-1">
        {filterChips.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setFilter(key)}
            className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-medium transition-colors border ${
              filter === key
                ? 'bg-tq-green text-white border-tq-green'
                : 'bg-white text-tq-muted border-tq-border hover:border-tq-green/40'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-12">
          <History className="h-10 w-10 text-tq-muted mx-auto mb-3 opacity-40" />
          <p className="text-sm text-tq-muted">Aucune activité pour ce filtre.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((it) => (
            <div
              key={it.id}
              className="flex items-center justify-between rounded-lg border border-tq-border px-4 py-3 bg-white"
            >
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div
                  className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                    it.kind === 'payment'
                      ? 'bg-tq-green-lighter dark:bg-green-950'
                      : it.kind === 'credit'
                        ? it.amount && it.amount > 0
                          ? 'bg-green-50 dark:bg-green-950'
                          : 'bg-red-50 dark:bg-red-950'
                        : 'bg-tq-bg'
                  }`}
                >
                  {it.kind === 'payment' ? (
                    <CreditCard className="h-4 w-4 text-tq-green" />
                  ) : it.kind === 'credit' ? (
                    it.amount && it.amount > 0 ? (
                      <ArrowDownLeft className="h-4 w-4 text-tq-green" />
                    ) : (
                      <ArrowUpRight className="h-4 w-4 text-red-500" />
                    )
                  ) : (
                    <FileText className="h-4 w-4 text-tq-muted" />
                  )}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-tq-text truncate">{it.title}</p>
                  <p className="text-xs text-tq-muted">
                    {it.subtitle ? `${it.subtitle} · ` : ''}
                    {formatDateTime(it.date)}
                  </p>
                </div>
              </div>
              {/* Argent et crédits ont chacun leur rendu : un montant payé n'est
                  ni signé ni exprimé en crédits. */}
              {it.kind === 'payment' && it.money && (
                <span className="text-sm font-semibold ml-3 shrink-0 text-tq-text">
                  {formatMoney(it.money.amount, it.money.currency)}
                </span>
              )}
              {it.kind === 'credit' && it.amount != null && (
                <span
                  className={`text-sm font-semibold ml-3 shrink-0 ${
                    it.amount > 0 ? 'text-tq-green' : 'text-red-600'
                  }`}
                >
                  {it.amount > 0 ? '+' : '-'}
                  {formatCredits(Math.abs(it.amount))}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
